'use client';

import { c as _c } from "react/compiler-runtime";
import { Chip, Spinner } from '@common/components';
import { INTERACTIVE_SURFACE } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { useSearchParams } from 'next/navigation';
import { Suspense, useRef } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { AFFILIATE_DISCLOSURE, MERCHANT_TAB } from '../_constants';
import { type AffiliateProductGroup, useAffiliateProductGroups } from '../_hooks/useAffiliateProductGroups';
import { resolveRewardRateLabel } from '../_utils/formatRewardRate';
import { MerchantListEmpty } from './MerchantListEmpty';
import { MerchantProductAllList } from './MerchantProductAllList';
import { MerchantProductCard, PRODUCT_GRID_CLASS_NAME } from './MerchantProductCard';
const GROUP_HEADER_ROW_CLASS_NAME = 'group flex w-full items-center justify-between gap-12 rounded-small px-12 py-4 text-left xl:py-12';
interface MerchantGroupHeaderProps {
  group: AffiliateProductGroup;
  onSelect?: () => void;
}
const MerchantGroupHeader = t0 => {
  const $ = _c(31);
  const {
    group,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== group) {
    t1 = resolveRewardRateLabel(group);
    $[0] = group;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rewardRateLabel = t1;
  const isPartnerPoint = group.rewardCurrencyType === "AFFILIATE_PARTNER_POINT";
  const visibleCount = group.hotDealList.length + group.giftProductList.length;
  let t2;
  if ($[2] !== group.logoImageUrl) {
    t2 = <Image src={group.logoImageUrl} alt="" width={50} height={50} className="size-50 shrink-0 rounded-large border border-Gray-100 bg-Gray-white object-contain p-4" />;
    $[2] = group.logoImageUrl;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== group.merchantName) {
    t3 = <span className="truncate font-600 text-Gray-900 typo-body4 xl:typo-body3">{group.merchantName}</span>;
    $[4] = group.merchantName;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== isPartnerPoint || $[7] !== rewardRateLabel) {
    t4 = rewardRateLabel ? <span className="flex shrink-0 items-center gap-2"><PointCoin currency={isPartnerPoint ? "AFFILIATE_PARTNER_POINT" : "WELLO_POINT"} size={20} /><span className={cn("font-600 typo-body4", isPartnerPoint ? "text-B-550" : "text-Y-800")}>{rewardRateLabel}</span></span> : null;
    $[6] = isPartnerPoint;
    $[7] = rewardRateLabel;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <span className="flex min-w-0 items-center gap-4">{t3}{t4}</span>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== group.industryName) {
    t6 = <span className="truncate font-600 text-Gray-600 typo-caption1">{group.industryName}</span>;
    $[12] = group.industryName;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <span className="flex min-w-0 flex-col">{t5}{t6}</span>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t2 || $[18] !== t7) {
    t8 = <span className="flex min-w-0 items-center gap-16">{t2}{t7}</span>;
    $[17] = t2;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== group.totalCount || $[21] !== onSelect || $[22] !== visibleCount) {
    t9 = onSelect && group.totalCount > visibleCount ? <span className="flex shrink-0 items-center gap-2 font-600 text-Gray-600 typo-caption1">{group.totalCount}개 모두보기<SvgChevronright className="size-16 text-Gray-400 transition group-hover:translate-x-1 group-hover:text-Gray-700 group-focus-visible:translate-x-1 group-focus-visible:text-Gray-700" /></span> : null;
    $[20] = group.totalCount;
    $[21] = onSelect;
    $[22] = visibleCount;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t8 || $[25] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const content = t10;
  let t11;
  if ($[27] !== content || $[28] !== group.merchantCode || $[29] !== onSelect) {
    t11 = <h2 className="px-8">{onSelect ? <button onClick={onSelect} className={cn(GROUP_HEADER_ROW_CLASS_NAME, INTERACTIVE_SURFACE)}>{content}</button> : <CustomLink href={ROUTES.BENEFIT_MERCHANT_DETAIL.withSearchParams({
        dynamicPath: {
          merchantCode: group.merchantCode
        }
      })} className={cn(GROUP_HEADER_ROW_CLASS_NAME, INTERACTIVE_SURFACE)}>{content}</CustomLink>}</h2>;
    $[27] = content;
    $[28] = group.merchantCode;
    $[29] = onSelect;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
};
export const MerchantProductList = () => {
  const $ = _c(23);
  const {
    data: groups
  } = useAffiliateProductGroups();
  const router = useCustomRouter();
  const t0 = useSearchParams();
  let t1;
  if ($[0] !== t0) {
    t1 = t0.get("merchant");
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedMerchantCode = t1;
  const filterRef = useRef(null);
  if (groups.length === 0) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <MerchantListEmpty message={"\uC0C1\uD488\uC744 \uC900\uBE44\uD558\uACE0 \uC788\uC5B4\uC694."} />;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  let t2;
  if ($[3] !== groups || $[4] !== selectedMerchantCode) {
    let t3;
    if ($[6] !== selectedMerchantCode) {
      t3 = t4 => {
        const {
          merchantCode
        } = t4;
        return merchantCode === selectedMerchantCode;
      };
      $[6] = selectedMerchantCode;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = groups.find(t3);
    $[3] = groups;
    $[4] = selectedMerchantCode;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const selectedGroup = t2;
  let t3;
  if ($[8] !== router) {
    t3 = merchantCode_0 => {
      router.replace(ROUTES.BENEFIT_MERCHANTS.withSearchParams({
        searchParams: {
          tab: MERCHANT_TAB.PRODUCT,
          merchant: merchantCode_0
        }
      }), {
        scroll: false
      });
    };
    $[8] = router;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const selectMerchant = t3;
  const handleSelectGroup = merchantCode_1 => {
    selectMerchant(merchantCode_1);
    filterRef.current?.scrollIntoView({
      block: "start"
    });
  };
  const t4 = !selectedGroup;
  let t5;
  if ($[10] !== selectMerchant) {
    t5 = () => selectMerchant(undefined);
    $[10] = selectMerchant;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <Chip type="radio" name="merchant-filter" className="shrink-0" checked={t4} onChange={t5}>전체</Chip>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== groups || $[16] !== selectMerchant || $[17] !== selectedGroup?.merchantCode) {
    let t8;
    if ($[19] !== selectMerchant || $[20] !== selectedGroup?.merchantCode) {
      t8 = t9 => {
        const {
          merchantCode: merchantCode_2,
          merchantName,
          logoImageUrl
        } = t9;
        return <Chip key={merchantCode_2} type="radio" name="merchant-filter" className="shrink-0 gap-4" checked={selectedGroup?.merchantCode === merchantCode_2} onChange={() => selectMerchant(merchantCode_2)}><Image src={logoImageUrl} alt="" width={20} height={20} className="size-20 rounded-pill border border-Gray-300 bg-Gray-50 object-contain" />{merchantName}</Chip>;
      };
      $[19] = selectMerchant;
      $[20] = selectedGroup?.merchantCode;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    t7 = groups.map(t8);
    $[15] = groups;
    $[16] = selectMerchant;
    $[17] = selectedGroup?.merchantCode;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="px-20 pb-24 font-400 text-Gray-550 typo-caption1">{AFFILIATE_DISCLOSURE}</p>;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return <><div ref={filterRef} role="radiogroup" aria-label={"\uC81C\uD734\uBAB0 \uD544\uD130"} className="flex gap-8 overflow-x-auto px-20 pb-12 pt-20 hide-scrollbar webkit-scroll-touch xl:pt-24">{t6}{t7}</div>{selectedGroup ? <section className="flex flex-col gap-20 pb-24 pt-12"><MerchantGroupHeader group={selectedGroup} /><Suspense key={selectedGroup.merchantCode} fallback={<div className="flex min-h-240 items-center justify-center"><Spinner /></div>}><MerchantProductAllList merchantCode={selectedGroup.merchantCode} /></Suspense></section> : groups.map(group => <section key={group.merchantCode} className="flex flex-col gap-20 pb-24 pt-12"><MerchantGroupHeader group={group} onSelect={() => handleSelectGroup(group.merchantCode)} /><ul className={PRODUCT_GRID_CLASS_NAME}>{group.hotDealList.map(_temp)}{group.giftProductList.map(_temp2)}</ul></section>)}{t8}</>;
};
function _temp(hotDeal) {
  return <li key={hotDeal.affiliateHotDealId} className="flex"><MerchantProductCard hotDeal={hotDeal} /></li>;
}
function _temp2(giftProduct) {
  return <li key={giftProduct.affiliateGiftProductLinkId} className="flex"><MerchantProductCard giftProduct={giftProduct} /></li>;
}